import { purgeApolloCache } from '../app/apollo/getApolloClient';
import type { Dispatch, RootState } from '../app/redux/useAppSelector';
import {
  SESSION_ORGANIZATION,
  SESSION_START,
  SESSION_USER,
  SESSION_USER_SETTING,
} from '../constants/redux';
import type {
  CurrentSessionFragment,
  UserSettings,
} from '../graphql/generated/graphqlTypes';
import { init as initAnalytics } from '../utils/analytics';
import LoggedInTeams from '../utils/cookie/LoggedInTeams';
import { isElectron } from '../utils/desktopClient';
import Logger from '../utils/Logger';
import SocketWithBackoffReconnect from '../utils/SocketWithBackoffReconnect';
import { purgePersisted } from '../utils/persistRedux';

const SessionActions = {
  clearSession: () => {
    return (_: Dispatch, getState: () => RootState) => {
      const { session } = getState();
      localStorage.removeItem('authToken');

      if (session.organization.id) {
        LoggedInTeams.remove(session.organization.id);
      } else if (isElectron) {
        LoggedInTeams.removeAll();
      }

      purgePersisted();
      purgeApolloCache();
      if (session.socket) {
        session.socket.disconnect();
      }
      analytics.ready(() => {
        analytics.reset();
        window.Intercom?.('shutdown');
      });
    };
  },

  startSession: ({
    authToken,
    intercomHash,
    organization,
    user,
  }: CurrentSessionFragment) => {
    return (dispatch: Dispatch) => {
      localStorage.setItem('authToken', authToken);
      const socket = new SocketWithBackoffReconnect('/socket', {
        authToken,
        timeout: 30000,
      });

      socket.connect();
      LoggedInTeams.add(organization.id);
      installVendors({ organization, user, intercomHash });
      dispatch(SessionActions.setCurrentOrganization(organization));
      dispatch(SessionActions.setCurrentUser(user));
      dispatch({
        type: SESSION_START,
        authToken,
        intercomHash,
        socket,
      });
      return Promise.resolve();
    };
  },

  setCurrentOrganization: (
    organization: Partial<CurrentSessionFragment['organization']>,
  ) => {
    return (dispatch: Dispatch) =>
      dispatch({
        type: SESSION_ORGANIZATION,
        organization,
      });
  },

  setCurrentUser: (user: CurrentSessionFragment['user']) => {
    return (dispatch: Dispatch) => dispatch({ type: SESSION_USER, user });
  },

  setUserSetting: (newSettings: UserSettings) => {
    return (dispatch: Dispatch) => {
      return dispatch({
        type: SESSION_USER_SETTING,
        value: newSettings,
      });
    };
  },
};

function installVendors({
  organization,
  user,
  intercomHash,
}: {
  organization: CurrentSessionFragment['organization'];
  user: CurrentSessionFragment['user'];
  intercomHash: string;
}) {
  const { Vitally } = window;
  try {
    Logger.setContext(
      {
        id: user.id,
        username: user.name,
        email: user.email ?? undefined,
      },
      {
        id: organization.id,
        name: organization.name,
        host: organization.host,
      },
    );
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
  }
  try {
    if (
      Math.random() < 0.2 && // Mitigation for users with multiple tabs open
      Vitally &&
      process.env.NODE_ENV === 'production'
    ) {
      Vitally.nps('survey', {
        productName: 'Slab',
        autoLoadSegment: true,
        primaryColor: '#a21a68',
        delay: 10000,
        followUpTitle: ({ score }: { score: number }) => {
          if (score <= 6) {
            return 'We’re sorry to hear that but appreciate you sharing 🙏';
          }
          return 'Thank you for your feedback 🙏';
        },
        followUpSubtitle: ({ score }: { score: number }) => {
          if (score <= 6) {
            return `What's the most frustrating part of Slab that earned us a ${score}?`;
          }
          if (score <= 8) {
            return 'What can we do even better to earn a 9 or 10?';
          }
          return `What do you like most about Slab that earned us a ${score}?`;
        },
        placeholderText: ({ score }: { score: number }) => {
          if (score <= 6) {
            return 'Please feel free to be candid!';
          }
          if (score <= 8) {
            return 'We want to get there!';
          }
          return 'We want to know!';
        },
        thanksTitle: ({ score }: { score: number }) => {
          if (score <= 6) {
            return "Thanks. We'll do better!";
          }
          return 'You are amazing!';
        },
      });
    }
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
  }
  initAnalytics(organization, user, intercomHash);
}

export default SessionActions;
